/**
 * catalog-loading.ts — skeleton сетки каталога и обёртка fetch без CLS
 */
import { state, refs } from './state';
import type { FetchResult } from './catalog';
import { showFlash } from './toast';

const MIN_SKELETON = 4;
const MAX_SKELETON = 15;

function skeletonCount(): number {
  const el = refs.products;
  if (!el) return MIN_SKELETON;
  const cards = el.querySelectorAll('.product-card').length;
  const rows = el.querySelectorAll('.list-row').length;
  const n = Math.max(cards, rows, state.products.length);
  if (n <= 0) return MIN_SKELETON;
  return Math.min(MAX_SKELETON, Math.max(MIN_SKELETON, n));
}

function gridSkeletonHtml(count: number): string {
  let cards = '';
  for (let i = 0; i < count; i++) {
    cards += `
      <article class="product-card" aria-hidden="true">
        <div class="skeleton-block skeleton-image"></div>
        <div class="product-content">
          <div class="skeleton-block skeleton-title"></div>
          <div class="skeleton-block skeleton-price"></div>
          <div class="skeleton-block skeleton-btn"></div>
        </div>
      </article>`;
  }
  return `<div class="products-grid">${cards}</div>`;
}

function listSkeletonHtml(count: number): string {
  let rows = '';
  for (let i = 0; i < count; i++) {
    rows += `
      <div class="list-row skeleton-list-row" aria-hidden="true">
        <div class="skeleton-block skeleton-thumb"></div>
        <div class="skeleton-block skeleton-title"></div>
        <div class="skeleton-block skeleton-price"></div>
        <div class="skeleton-block skeleton-title"></div>
        <div class="skeleton-block skeleton-price"></div>
        <div class="skeleton-block skeleton-btn"></div>
      </div>`;
  }
  return `
    <section class="products-list card">
      <div class="list-header">
        <div class="list-header-product">Товар</div>
        <div class="center">Цена</div>
        <div class="center">Шт.</div>
        <div class="center">Кол-во</div>
        <div class="center">Сумма</div>
      </div>
      <div class="list-body">${rows}</div>
    </section>`;
}

export function showProductSkeletons(): void {
  const el = refs.products;
  if (!el) return;
  const h = el.offsetHeight;
  if (h > 0) {
    el.style.minHeight = `${h}px`;
  }
  el.setAttribute('aria-busy', 'true');
  const count = skeletonCount();
  el.innerHTML =
    state.viewMode === 'list' ? listSkeletonHtml(count) : gridSkeletonHtml(count);
}

export function clearProductLoading(): void {
  const el = refs.products;
  if (!el) return;
  el.removeAttribute('aria-busy');
  el.style.minHeight = '';
}

/**
 * Показать skeleton, выполнить fetch, снять skeleton (кроме stale).
 */
export async function runCatalogFetch(
  execute: () => Promise<FetchResult>,
): Promise<FetchResult> {
  showProductSkeletons();
  const result = await execute();
  if (result === 'stale') {
    return result;
  }
  clearProductLoading();
  if (result === 'error' && state.products.length > 0) {
    state.loadError = false;
    showFlash('Не удалось обновить список. Показаны прежние товары.', 'error');
  }
  return result;
}

/** Вызвать `done` только если fetch не устарел (stale). */
export async function runCatalogFetchThen(
  execute: () => Promise<FetchResult>,
  done: () => void,
): Promise<void> {
  const result = await runCatalogFetch(execute);
  if (result === 'stale') return;
  done();
}
